import type {
  ExternalRunProgressSnapshot,
  ExternalRunStreamStatus,
  ExternalShardLaunchState,
  ExternalShardPhase,
} from '@chatsdet/types';

/** Render-ready state for one shard, independent of its transport. */
export interface ExternalShardProgress {
  shardIndex: number;
  phase: ExternalShardPhase;
  /** 1-based; > 1 means this shard is being retried. */
  attempt: number;
  /** Whether any attempt of this shard has reported results yet. */
  hadResults: boolean;
  /** Durable launch outcome, when the progress snapshot supplies one. */
  launchState?: ExternalShardLaunchState;
}

/** SSE shard state intentionally excludes durable launch diagnostics. */
export type ExternalShardStreamProgress = Omit<ExternalShardProgress, 'launchState'>;

/** Transport-neutral state produced by the live stream before durable merge. */
export type ExternalRunStreamProgressState = Omit<ExternalRunProgressState, 'shards'> & {
  shards: ExternalShardStreamProgress[];
};

/** Combined render state assembled from durable progress and optional SSE updates. */
export interface ExternalRunProgressState {
  status: ExternalRunStreamStatus | null;
  shardTotal: number | null;
  revision: string | null;
  targetUrl: string | null;
  shards: ExternalShardProgress[];
  sealed: { status: string; total: number; passed: number; failed: number } | null;
  isLive: boolean;
}

const PHASE_RANK: Record<ExternalShardPhase, number> = {
  queued: 0,
  launching: 1,
  running: 2,
  collecting: 3,
  done: 4,
};

const STATUS_RANK: Record<ExternalRunStreamStatus, number> = {
  queued: 0,
  running: 1,
  collecting: 2,
  sealed: 3,
  canceled: 3,
  failed: 3,
};

function progressShards(progress: ExternalRunProgressSnapshot): ExternalShardProgress[] {
  return [...progress.shards]
    .sort((a, b) => a.shardIndex - b.shardIndex)
    .map((shard) => ({ ...shard, hadResults: false }));
}

function mergeShard(
  durable: ExternalShardProgress | undefined,
  live: ExternalShardStreamProgress | undefined
): ExternalShardProgress | undefined {
  if (!durable) return live;
  if (!live) return durable;
  const hadResults = durable.hadResults || live.hadResults;
  if (live.attempt > durable.attempt) {
    const { launchState: _staleLaunchState, ...durableWithoutLaunchState } = durable;
    return { ...durableWithoutLaunchState, ...live, hadResults };
  }
  if (durable.attempt > live.attempt) return { ...live, ...durable, hadResults };

  // SSE is normally the freshest source within an attempt, but a replay can
  // contain an older event. Never let a lower phase rank move the chip back.
  const winner = PHASE_RANK[live.phase] >= PHASE_RANK[durable.phase] ? live : durable;
  const merged = { ...durable, ...live, ...winner, hadResults };
  // SSE intentionally has no durable launch state. Once it wins within the
  // same attempt, remove any label copied from an older durable snapshot.
  if (winner === live) {
    const { launchState: _staleLaunchState, ...withoutLaunchState } = merged;
    return withoutLaunchState;
  }
  return merged;
}

function progressStatus(
  progress: ExternalRunProgressSnapshot,
  shards: readonly ExternalShardProgress[]
): ExternalRunStreamStatus {
  if (progress.run.status === 'sealed' || progress.run.status === 'canceled') {
    return progress.run.status;
  }
  if (progress.run.status === 'collecting') return 'collecting';
  if (shards.some((shard) => shard.phase === 'collecting')) return 'collecting';
  if (shards.some((shard) => PHASE_RANK[shard.phase] >= PHASE_RANK.running)) return 'running';
  return 'queued';
}

/** Merge the embedded durable projection with SSE without allowing regressions. */
export function mergeExternalRunProgress(
  stream: ExternalRunStreamProgressState,
  progress: ExternalRunProgressSnapshot | null
): ExternalRunProgressState {
  if (!progress) return stream;
  const durableShards = progressShards(progress);
  const shardIndexes = new Set([
    ...durableShards.map((shard) => shard.shardIndex),
    ...stream.shards.map((shard) => shard.shardIndex),
  ]);
  const shards = [...shardIndexes]
    .sort((a, b) => a - b)
    .map((shardIndex) =>
      mergeShard(
        durableShards.find((shard) => shard.shardIndex === shardIndex),
        stream.shards.find((shard) => shard.shardIndex === shardIndex)
      )
    )
    .filter((shard): shard is ExternalShardProgress => shard !== undefined);

  const durableStatus = progressStatus(progress, shards);
  const status =
    stream.status === null || STATUS_RANK[durableStatus] >= STATUS_RANK[stream.status]
      ? durableStatus
      : stream.status;
  return {
    ...stream,
    status,
    shardTotal: progress.run.shardTotal ?? stream.shardTotal ?? durableShards.length,
    shards,
    isLive: stream.isLive || durableShards.length > 0 || progress.run.shardTotal !== null,
  };
}
